<template>
  <div class="our-story-container relative min-h-screen pb-20 overflow-hidden">
    <!-- Background Elements -->
    <div class="absolute inset-0 bg-pattern opacity-10"></div>
    <div class="absolute top-0 left-0 w-1/3 h-1/2 bg-gradient-radial from-primary/20 to-transparent opacity-20 blur-3xl"></div>
    <div class="absolute bottom-0 right-0 w-1/2 h-1/3 bg-gradient-radial from-secondary/10 to-transparent opacity-20 blur-3xl"></div>
    
    <!-- Decorative Elements -->
    <div class="absolute left-0 top-1/4 h-[60%] w-1 bg-gradient-to-b from-transparent via-primary/30 to-transparent"></div>
    <div class="absolute right-0 top-1/3 h-[40%] w-1 bg-gradient-to-b from-transparent via-secondary/30 to-transparent"></div>
    <div class="steel-accent top-accent"></div>
    <div class="steel-accent bottom-accent"></div>
    
    <!-- Floating Image Elements -->
    <div class="floating-images">
      <div 
        v-for="(image, index) in floatingImages" 
        :key="index"
        class="floating-image"
        :class="`image-${index + 1}`"
        :style="{ backgroundImage: `url(${image.src})` }"
        @mousemove="handleFloatingImageTilt($event, index)"
        @mouseleave="resetFloatingImageTilt(index)"
      >
        <div class="image-border"></div>
      </div>
    </div>
    
  <!-- Section Separator -->
  <div class="section-separator">
    <div class="separator-content">
      <div class="separator-line left"></div>
      <div class="separator-emblem">
        <div class="emblem-outer"></div>
        <div class="emblem-inner">
          <div class="emblem-icon"></div>
        </div>
      </div>
      <div class="separator-line right"></div>
    </div>
    <div class="separator-triangles">
      <div class="triangle-left"></div>
      <div class="triangle-right"></div>
    </div>
  </div>

    <!-- Steel Accent Elements -->
    <div class="steel-accent left"></div>
    <div class="steel-accent right"></div>
    <div class="steel-accent top"></div>
    <div class="steel-accent bottom"></div>

    <!-- Header Section with Parallax -->
    <div 
      class="timeline-header relative z-10 pt-[15vh] px-6 text-left mb-12 opacity-0 transform translate-y-8"
      ref="header"
      @mousemove="handleParallax($event, 'header')"
      :class="{'fade-in': isInView.header}"
    >
      <div class="header-accent-line"></div>
      <div class="header-accent-circle"></div>
      
      <h2 class="text-secondary/70 text-2xl md:text-3xl mb-2 font-light tracking-wide">{{ firstLineText }}</h2>
      <h1 class="text-white text-4xl md:text-6xl font-bold relative inline-block">
        <span class="relative z-10">{{ secondLineText }}</span>
        <span class="absolute bottom-0 left-0 w-full h-1 bg-primary transform scale-x-0 origin-left transition-transform duration-1000"
              :class="{'scale-x-100': isInView.header}"></span>
      </h1>
      <div class="mt-8 max-w-2xl text-left">
        <p class="text-white/70 text-lg md:text-xl leading-relaxed">
          The story of excellence in American manufacturing, built on a foundation of innovation, quality, and dedication.
        </p>
      </div>
    </div>
    
    <!-- Timeline Container -->
    <div class="timeline-wrapper relative max-w-7xl mx-auto px-4 mt-20">
      <!-- Timeline Line -->
      <div class="timeline-line"></div>
      
      <!-- History Sections -->
      <div class="timeline-grid">
        <!-- Section 1: The Beginning -->
        <div class="timeline-section section-right" ref="section1" :class="{'fade-in': isInView.section1}">
          <div class="section-year">
            <span class="year-marker">1966</span>
            <div class="year-line"></div>
          </div>
          
          <div class="section-content">
            <div class="content-card glassmorphism">
              <h3 class="card-title">The Beginning</h3>
              <div class="accent-line"></div>
              <p class="text-secondary/90 text-xl md:text-2xl font-light leading-tight mb-4">
                Stainless Tank & Equipment may not have the smoothest history, but it has chugged its way through the decades, delivering a quiet reliability to its customers.
              </p>
              <p class="text-white/80 text-base md:text-lg leading-relaxed mb-4">
                Alex Falch and Don Rostad founded Stainless Tank & Equipment Company (STE) in 1966 in Sun Prairie, Wisconsin, after previously working for Dairy Equipment Company in nearby Madison. Paul Krueger, who perfected the design of STE's chemical, dairy, and sanitary tank trailers as chief engineer, joined STE as part owner in 1973.
              </p>
              <p class="text-white/80 text-base md:text-lg leading-relaxed">
                From humble beginnings in a garage in South Central Wisconsin, STE set out to deliver a higher standard of engineering and manufacturing excellence in the liquid tank industry. The STE brand built its reputation by meeting and exceeding the most demanding bulk hauling requirements for the chemical, milk, and sanitary transport industries.
              </p>
            </div>
          </div>
        </div>
        
        <!-- Section 2: American Craftsmanship -->
        <div class="timeline-section section-left" ref="section2" :class="{'fade-in': isInView.section2}">
          <div class="section-year">
            <span class="year-marker">1990s</span>
            <div class="year-line"></div>
          </div>
          
          <div class="section-content">
            <div class="content-card glassmorphism">
              <h3 class="card-title">American Craftsmanship</h3>
              <div class="accent-line"></div>
              <p class="text-secondary/90 text-xl md:text-2xl font-light leading-tight mb-4">
                Our equipment is designed with purpose and fabricated by skilled craftsmen right here in America. At STE, we are proud of our US-based manufacturing and our US-sourced supply chain.
              </p>
              <p class="text-white/80 text-base md:text-lg leading-relaxed mb-4">
                Today, STE's equipment is synonymous with dependability, longevity and robustness. And, because of STE's constant innovations in technology and manufacturing processes, we are recognized throughout North America as a leader in bulk tank trailer design. In fact, our equipment is trusted to haul some of the most dangerous substances on earth. Why? Because the multi-national chemical companies we work with don't take chances with safety. They rely on STE's critical engineering and quality processes for their vessels.
              </p>
              <p class="text-white/80 text-base md:text-lg leading-relaxed">
                Based in Wisconsin, STE has a reputation for meeting and exceeding the most demanding bulk hauling requirements of the chemical, milk, and sanitary transport industries. Our equipment is built to work harder, last longer, and retain its value years down the road.
              </p>
            </div>
          </div>
        </div>
        
        <!-- Section 3: Growth & Innovation -->
        <div class="timeline-section section-right" ref="section3" :class="{'fade-in': isInView.section3}">
          <div class="section-year">
            <span class="year-marker">2006</span>
            <div class="year-line"></div>
          </div>
          
          <div class="section-content">
            <div class="content-card glassmorphism">
              <h3 class="card-title">Growth & Innovation</h3>
              <div class="accent-line"></div>
              <p class="text-secondary/90 text-xl md:text-2xl font-light leading-tight mb-4">
                Since 2006, under the Hendricks Holding Company umbrella, STE has grown its brand to include additional trailer models and an expanded service department.
              </p>
              <p class="text-white/80 text-base md:text-lg leading-relaxed mb-4">
                When STE fell on hard times, the Hendricks co-founders rescued it from receivership in 2006. "It employed a lot of hard-working people, and they didn't want those jobs to go away," explained Brent Fox, the Hendricks chairman's eldest son. "So they chose to find a way to make STE work."
              </p>
              <p class="text-white/80 text-base md:text-lg leading-relaxed mb-6">
                Hendricks Holding Company, Inc. (HHC) is a private holding company that invests in, and oversees diverse, financially strong, and sustainable companies. They support their companies by providing resources to support them in meeting their business goals and delivering best-in-class services to their respective industries.
              </p>
              
              <a 
                href="https://www.hendricksholding.com/"
                target="_blank"
                rel="noopener noreferrer"
                class="hendricks-button group"
                @mouseenter="btnHovered = true"
                @mouseleave="btnHovered = false"
              >
                <span class="btn-text" :class="{'btn-text-animate': btnHovered}">
                  Learn More About Hendricks Holding
                </span>
                <span class="btn-icon" :class="{'btn-icon-animate': btnHovered}">
                  <font-awesome-icon :icon="['fas', 'arrow-right']" />
                </span>
                <div class="btn-bg" :class="{'btn-bg-animate': btnHovered}"></div>
              </a>
            </div>
          </div>
        </div>
        
        <!-- Section 4: The STE Advantage -->
        <div class="timeline-section section-left" ref="section4" :class="{'fade-in': isInView.section4}">
          <div class="section-year">
            <span class="year-marker">2010s</span>
            <div class="year-line"></div>
          </div>
          
          <div class="section-content">
            <div class="content-card glassmorphism">
              <h3 class="card-title">The STE Advantage</h3>
              <div class="accent-line"></div>
              <p class="text-secondary/90 text-xl md:text-2xl font-light leading-tight mb-4">
                Our equipment is built to work harder, last longer, and retain its value years down the road.
              </p>
              <p class="text-white/80 text-base md:text-lg leading-relaxed mb-4">
                STE provides premium quality to every bulk tank and trailer we build. Constructed by America's best engineers and welders, STE provides custom equipment that meets all your specifications and expectations. Skilled American workers dedicate themselves to providing you with a product that is built to last.
              </p>
              <p class="text-white/80 text-base md:text-lg leading-relaxed">
                STE continues to build on its legacy of excellence, combining traditional American craftsmanship with cutting-edge technology. Our commitment to quality, safety, and customer satisfaction remains at the heart of everything we do. With a focused vision on innovation and sustainability, STE is positioned to lead the industry into the future.
              </p>
            </div>
          </div>
        </div>
        
        <!-- Section 5: Premium Quality -->
        <div class="timeline-section section-right" ref="section5" :class="{'fade-in': isInView.section5}">
          <div class="section-year">
            <span class="year-marker">2020</span>
            <div class="year-line"></div>
          </div>
          
          <div class="section-content">
            <div class="content-card glassmorphism">
              <h3 class="card-title">Premium Quality</h3>
              <div class="accent-line"></div>
              <p class="text-secondary/90 text-xl md:text-2xl font-light leading-tight mb-4">
                STE is consistently recognized for manufacturing some of the best and most rugged tank trailers in the industry.
              </p>
              <p class="text-white/80 text-base md:text-lg leading-relaxed mb-4">
                STE is dedicated to keeping your costs down and your equipment operating at optimum levels. That's why we offer genuine OEM parts that exactly duplicate what's on your equipment. STE laser-etches part numbers on all STE manufactured pieces which is tied to our MRP system. So, when an OEM part gets damaged, STE can go to work quickly to fabricate a replacement using original 3D engineering drawings and measurements.
              </p>
              <p class="text-white/80 text-base md:text-lg leading-relaxed">
                Our equipment is trusted by the most demanding industries, and our reputation for quality and reliability continues to grow. Designed by people that know trailers and customized for your specific needs without sacrificing quality or cutting corners.
              </p>
            </div>
          </div>
        </div>
        
        <!-- Section 6: The Future -->
        <div class="timeline-section section-left" ref="section6" :class="{'fade-in': isInView.section6}">
          <div class="section-year">
            <span class="year-marker">2025</span>
            <div class="year-line"></div>
          </div>
          
          <div class="section-content">
            <div class="content-card glassmorphism">
              <h3 class="card-title">The Future</h3>
              <div class="accent-line"></div>
              <p class="text-secondary/90 text-xl md:text-2xl font-light leading-tight mb-4">
                Investing in American manufacturing with a state-of-the-art facility in Beloit, Wisconsin.
              </p>
              <p class="text-white/80 text-base md:text-lg leading-relaxed mb-4">
                Stainless Tank & Equipment (STE) built a new state-of-the-art manufacturing facility off of Shopiere Road/County Road S adjacent to Interstate 90. This represents a significant investment in the Beloit area community and will allow for a 10X growth potential for STE.
              </p>
              <p class="text-white/80 text-base md:text-lg leading-relaxed mb-4">
                "The investment by Hendricks Holding Company, Inc. allows STE to continue to grow its strength, product offering, and presence in the tank trailer industry. This investment also gives STE the capability to access new markets and increase value for our customers through higher quality engineered products," said Chris Sipe, President of STE.
              </p>
              <p class="text-white/80 text-base md:text-lg leading-relaxed">
                With this new advanced 400,000-sq.-ft. facility, STE will continue its commitment to American manufacturing, combining traditional craftsmanship with cutting-edge technology to produce the highest quality tank trailers in the industry.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'OurStory',
  data() {
    return {
      firstLineText: 'A growing tree',
      secondLineText: 'With reliable roots',
      observers: [],
      isInView: {
        header: false,
        section1: false,
        section2: false,
        section3: false,
        section4: false,
        section5: false,
        section6: false
      },
      btnHovered: false,
      tiltElements: {},
      floatingImages: [
        { src: '/img/History.jpg', tilt: { x: 0, y: 0 } },
        { src: '/img/HistoryRight1.jpg', tilt: { x: 0, y: 0 } },
        { src: '/img/ourstory/AmericanCraftsmen.jpg', tilt: { x: 0, y: 0 } },
        { src: '/img/ourstory/AmericanCraftsmenB.jpg', tilt: { x: 0, y: 0 } },
        { src: '/img/STEAdvantage.jpg', tilt: { x: 0, y: 0 } },
        { src: '/img/WeldingFront.png', tilt: { x: 0, y: 0 } },
        { src: '/img/TakeaTour.jpg', tilt: { x: 0, y: 0 } },
        { src: '/img/ourstory/quality_products1.jpg', tilt: { x: 0, y: 0 } },
        { src: '/img/ourstory/the_future1.jpg', tilt: { x: 0, y: 0 } },
        { src: '/img/ourstory/the_future2.jpg', tilt: { x: 0, y: 0 } },
      ]
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.setupIntersectionObservers();
      this.initTiltElements();
      
      // Add initial entrance animation
      setTimeout(() => {
        this.isInView.header = true;
      }, 500);
    });
  },
  beforeUnmount() {
    this.observers.forEach(observer => observer.disconnect());
  },
  methods: {
    setupIntersectionObservers() {
      const sections = [
        { ref: 'header', threshold: 0.3 },
        { ref: 'section1', threshold: 0.2 },
        { ref: 'section2', threshold: 0.2 },
        { ref: 'section3', threshold: 0.2 },
        { ref: 'section4', threshold: 0.2 },
        { ref: 'section5', threshold: 0.2 },
        { ref: 'section6', threshold: 0.2 }
      ];
      
      sections.forEach(section => {
        const element = this.$refs[section.ref];
        if (!element) return;
        
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.isInView[section.ref] = true;
            } else if (section.ref !== 'header') {
              // Keep the header visible once shown
              this.isInView[section.ref] = false;
            }
          });
        }, { 
          threshold: section.threshold,
          rootMargin: '0px 0px -100px 0px'
        });
        
        observer.observe(element);
        this.observers.push(observer);
      });
    },

    handleParallax(e, elementType) {
      const element = this.$refs[elementType];
      if (!element) return;
      
      const rect = element.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      
      // Calculate distance from center
      const distanceX = (e.clientX - centerX) / (rect.width / 2);
      const distanceY = (e.clientY - centerY) / (rect.height / 2);
      
      // Apply parallax effect based on element type
      if (elementType === 'header') {
        element.style.transform = `translate(${distanceX * -10}px, ${distanceY * -10}px)`;
      }
    },
    
    initTiltElements() {
      const imageRefs = ['image1', 'image2', 'image3', 'image4'];
      imageRefs.forEach(ref => {
        if (this.$refs[ref]) {
          this.tiltElements[ref] = {
            element: this.$refs[ref],
            transforms: { rotateX: 0, rotateY: 0, scale: 1 }
          };
        }
      });
    },
    
    handleFloatingImageTilt(e, index) {
      const target = e.currentTarget;
      const rect = target.getBoundingClientRect();
      
      // Calculate mouse position relative to the element (from -1 to 1)
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = ((e.clientY - rect.top) / rect.height) * 2 - 1;
      
      // Calculate tilt amount
      const tiltX = y * -5;
      const tiltY = x * 5;
      
      // Store tilt values
      this.floatingImages[index].tilt = { x: tiltX, y: tiltY };
      
      // Apply transformation
      target.style.transform = `perspective(1000px) rotateX(${tiltX}deg) rotateY(${tiltY}deg) scale3d(1.05, 1.05, 1.05)`;
      
      // Apply a subtle movement to the image inside for a parallax effect
      const image = target.querySelector('.image-border');
      if (image) {
        image.style.transform = `translate(${x * -5}px, ${y * -5}px)`;
      }
    },
    
    resetFloatingImageTilt(index) {
      const image = document.querySelector(`.image-${index + 1}`);
      if (image) {
        image.style.transform = '';
        
        const border = image.querySelector('.image-border');
        if (border) {
          border.style.transform = '';
        }
      }
      
      // Reset stored tilt values
      this.floatingImages[index].tilt = { x: 0, y: 0 };
    },
    
    handleImageTilt(e) {
      const target = e.currentTarget;
      const rect = target.getBoundingClientRect();
      
      // Calculate mouse position relative to the element (from -1 to 1)
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = ((e.clientY - rect.top) / rect.height) * 2 - 1;
      
      // Calculate tilt amount
      const tiltX = y * -8; // Reversed for correct tilt direction
      const tiltY = x * 8;
      
      // Apply transformation
      target.style.transform = `perspective(1000px) rotateX(${tiltX}deg) rotateY(${tiltY}deg) scale3d(1.02, 1.02, 1.02)`;
      
      // Move corners based on mouse position for an extra effect
      const corners = target.querySelectorAll('.card-corner');
      corners.forEach(corner => {
        const isLeft = corner.classList.contains('top-left') || corner.classList.contains('bottom-left');
        const isTop = corner.classList.contains('top-left') || corner.classList.contains('top-right');
        
        const offsetX = isLeft ? -x * 5 : x * 5;
        const offsetY = isTop ? -y * 5 : y * 5;
        
        corner.style.transform = `translate(${offsetX}px, ${offsetY}px)`;
      });
      
      // Apply a subtle movement to the image inside for a parallax effect
      const image = target.querySelector('img');
      if (image) {
        image.style.transform = `translate(${x * -10}px, ${y * -10}px) scale(1.1)`;
      }
    },
    
    resetTilt(e) {
      const target = e.currentTarget;
      target.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
      
      // Reset corners
      const corners = target.querySelectorAll('.card-corner');
      corners.forEach(corner => {
        corner.style.transform = 'translate(0, 0)';
      });
      
      // Reset image
      const image = target.querySelector('img');
      if (image) {
        image.style.transform = 'translate(0, 0) scale(1.05)';
      }
    }
  }
};
</script>

<style scoped>
/* Base Styles */
.our-story-container {
  color: white;
  will-change: transform;
}

.bg-pattern {
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

/* Floating Images Styling - Updated */
.floating-images {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 5;
  pointer-events: none;
}

.floating-image {
  position: absolute;
  width: 480px;  /* Increased from 220px */
  height: 360px; /* Increased from 150px */
  background-size: cover;
  background-position: center;
  border-radius: 12px; /* Increased from 8px */
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.6); /* Enhanced shadow */
  opacity: 0.8; /* Increased from 0.6 */
  transition: all 0.3s ease;
  cursor: pointer;
  pointer-events: auto;
  transform-style: preserve-3d;
  overflow: hidden;
}

.floating-image:hover {
  opacity: 1;
  z-index: 20; /* Increased from 10 */
  transform: scale(1.05);
}

.image-border {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 3px solid var(--color-primary); /* Increased from 2px */
  border-radius: 12px;
  box-sizing: border-box;
  transition: transform 0.3s ease;
}

/* Position each floating image - Repositioned to opposite sides */
.image-1 {
  top: 10%;
  left: 100px; /* Moved to right instead of left */
  transform: rotate(-3deg);
  z-index: 6;
}

.image-2 {
  top: 17%;
  left: 35px; /* Moved to left instead of right */
  transform: rotate(4deg);
  z-index: 5;
  height:260px;
}

.image-3 {
  top: 26%;
  right: 200px; /* Moved to right for section 3 */
  transform: rotate(-5deg);
  z-index: 6;
}

.image-4 {
  top: 32%;
  right: 145px; /* Moved to right for section 3 */
  transform: rotate(5deg);
  z-index: 6;
}

.image-5 {
  top: 47%;
  left: 100px; /* Moved to right for section 3 */
  transform: rotate(-5deg);
  z-index: 6;
}

.image-6 {
  top: 40%;
  left: 45px; /* Moved to left for section 4 */
  transform: rotate(5deg);
  z-index: 5;
}

.image-7 {
  top: 60%;
  right: 120px; /* Positioned for section 5 */
  transform: rotate(-4deg) scale(0.95);
  z-index: 6;
}

.image-8 {
  top: 70%;
  left: 270px; /* Positioned for section 6 */
  transform: rotate(6deg) scale(0.95);
  z-index: 5;
}

.image-9 {
  top: 83%;
  right: 50px; /* Additional image for visual balance */
  transform: rotate(-7deg) scale(0.9);
  z-index: 6;
}

.image-10 {
  top: 90%;
  right: 150px; /* Additional image for visual balance */
  transform: rotate(7deg) scale(0.9);
  z-index: 5;
}

/* Steel Accent Elements */
.steel-accent {
  position: absolute;
  opacity: 0.1;
  z-index: 1;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.5), rgba(200, 200, 200, 0.2));
}

.top-accent {
  top: 0;
  right: 10%;
  width: 30%;
  height: 2px;
  transform: skewX(-30deg);
}

.bottom-accent {
  bottom: 0;
  left: 10%;
  width: 30%;
  height: 2px;
  transform: skewX(30deg);
}

/* Header Styles */
.timeline-header {
  transition: opacity 1s ease, transform 1s ease;
  margin-left: 10%;
  max-width: 80%;
}

.header-accent-line {
  width: 120px;
  height: 3px;
  background: linear-gradient(90deg, var(--color-primary), transparent);
  margin-bottom: 1.5rem;
}

.header-accent-circle {
  position: absolute;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  border: 1px solid rgba(143, 0, 0, 0.1);
  top: 50%;
  left: 30%;
  transform: translate(-50%, -50%);
  opacity: 0.3;
  animation: rotate 30s linear infinite;
  pointer-events: none;
}

.header-accent-circle::before {
  content: '';
  position: absolute;
  width: 250px;
  height: 250px;
  border-radius: 50%;
  border: 1px dashed rgba(143, 0, 0, 0.05);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation: rotate 40s linear infinite reverse;
}

/* Timeline Styles */
.timeline-wrapper {
  position: relative;
}

.timeline-line {
  position: absolute;
  left: 50%;
  top: 0;
  bottom: 0;
  width: 2px;
  background: linear-gradient(to bottom, 
    transparent, 
    rgba(143, 0, 0, 0.3) 10%, 
    rgba(143, 0, 0, 0.3) 90%, 
    transparent
  );
  transform: translateX(-1px);
  z-index: 1;
}

.timeline-section {
  position: relative;
  margin-bottom: 150px;
  opacity: 0;
  transform: translateY(50px);
  transition: all 1s cubic-bezier(0.23, 1, 0.32, 1);
}

.timeline-section.fade-in {
  opacity: 1;
  transform: translateY(0);
}

.section-year {
  position: absolute;
  top: 40px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.year-marker {
  padding: 5px 15px;
  background: var(--color-primary);
  color: white;
  font-weight: bold;
  border-radius: 30px;
  box-shadow: 0 4px 20px rgba(143, 0, 0, 0.4);
  white-space: nowrap;
}

.year-line {
  width: 2px;
  height: 30px;
  background: var(--color-primary);
  margin-top: 5px;
}

.section-content {
  display: flex;
  justify-content: space-between;
  gap: 30px;
}

/* Left/Right Section Positioning */
.section-right .section-content {
  padding-left: calc(50% + 40px);
  flex-direction: row;
}

.section-left .section-content {
  padding-right: calc(50% + 40px);
  flex-direction: row;
}

/* Content Card Styling */
.content-card {
  width: 100%;
  padding: 30px;
  border-radius: 8px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  text-align: left;
}

.glassmorphism {
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.glassmorphism:hover {
  background: rgba(255, 255, 255, 0.05);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.card-title {
  font-size: 1.75rem;
  font-weight: bold;
  margin-bottom: 0.75rem;
  color: white;
}

.accent-line {
  width: 60px;
  height: 3px;
  background: var(--color-primary);
  margin-bottom: 1.5rem;
  transition: width 0.3s ease;
}

.content-card:hover .accent-line {
  width: 100px;
}

/* Button Styling */
.hendricks-button {
  position: relative;
  padding: 12px 24px;
  border-radius: 6px;
  font-weight: bold;
  color: white;
  overflow: hidden;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: transparent;
  border: 1px solid rgba(143, 0, 0, 0.5);
  transition: all 0.3s ease;
}

.btn-text {
  position: relative;
  z-index: 2;
  transition: transform 0.3s ease;
}

.btn-text-animate {
  transform: translateX(-5px);
}

.btn-icon {
  position: relative;
  z-index: 2;
  opacity: 0.8;
  transition: all 0.3s ease;
}

.btn-icon-animate {
  transform: translateX(5px);
  opacity: 1;
}

.btn-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(90deg, var(--color-primary), #690000);
  z-index: 1;
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.3s ease;
}

.btn-bg-animate {
  transform: scaleX(1);
}

.hendricks-button:hover {
  box-shadow: 0 5px 15px rgba(143, 0, 0, 0.3);
  border-color: var(--color-primary);
}

/* Animations */
.fade-in {
  opacity: 1 !important;
  transform: translateY(0) !important;
}

@keyframes rotate {
  from {
    transform: translate(-50%, -50%) rotate(0deg);
  }
  to {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}

@keyframes float {
  0%, 100% { transform: translateY(0) rotate(var(--rotation, 0deg)); }
  50% { transform: translateY(-15px) rotate(var(--rotation, 0deg)); }
}

/* Responsive Styles - Updated */
@media (max-width: 1440px) {
  .floating-image {
    width: 340px;
    height: 230px;
  }
  
  .image-1 { right: -50px; }
  .image-2 { left: -50px; }
  .image-3 { right: -40px; }
  .image-4 { right: -40px; }
  .image-5 { right: -40px; }
  .image-6 { left: -40px; }
  .image-7 { right: -45px; }
  .image-8 { left: -45px; }
  .image-9 { right: -35px; }
  .image-10 { right: -35px; }
}

@media (max-width: 1280px) {
  .floating-image {
    width: 400px;
    height: 300px;
  }
  
  .image-1 { right: -30px; }
  .image-2 { left: -30px; }
  .image-3 { right: -25px; }
  .image-4 { right: -25px; }
  .image-5 { right: -25px; }
  .image-6 { left: -25px; }
  .image-7 { right: -35px; }
  .image-8 { left: -35px; }
  .image-9 { right: -20px; }
  .image-10 { right: -20px; }
}

@media (max-width: 1024px) {
  .section-right .section-content,
  .section-left .section-content {
    padding-left: 0;
    padding-right: 0;
    flex-direction: column;
    gap: 20px;
  }
  
  .content-card {
    width: 100%;
  }
  
  .timeline-line {
    left: 20px;
  }
  
  .section-year {
    right: 30px;
    transform: none;
    align-items: flex-end;
  }

  .section-year .year-line {
    display: none;
  }
  
  .floating-image {
    display:none;
  }
  
  /* For tablet and mobile, update positioning to avoid crowding */
  .image-1 { right: -20px; top: 18%; }
  .image-2 { left: -20px; top: 32%; }
  .image-3 { right: -20px; top: 46%; }
  .image-4 { right: -20px; top: 46%; }
  .image-5 { right: -20px; top: 46%; }
  .image-6 { left: -20px; top: 60%; }
  .image-7 { right: -20px; top: 74%; }
  .image-8 { left: -20px; top: 88%; }
  .image-9, .image-10 { display: none; }
}

@media (max-width: 768px) {
  .timeline-header {
    padding-top: 10vh;
  }
  
  .card-title {
    font-size: 1.5rem;
  }
  
  .floating-image {
    width: 180px;
    height: 120px;
  }
  
  .image-1 { right: -15px; }
  .image-2 { left: -15px; }
  .image-3 { right: -15px; }
  .image-4 { left: -15px; }
  .image-5 { right: -15px; }
  .image-6 { left: -15px; }
}

@media (max-width: 640px) {
  .timeline-header {
    padding-top: 5vh;
  }
  
  .floating-images {
    display: none;
  }
}

/* Steel accent elements */
.steel-accent {
  position: absolute;
  background: linear-gradient(135deg, rgba(183, 185, 186, 0.3), rgba(100, 100, 110, 0.1));
  z-index: 5;
}

.steel-accent.left {
  top: 30%;
  left: 0;
  width: 40px;
  height: 40%;
  clip-path: polygon(0 0, 100% 20%, 100% 80%, 0 100%);
}

.steel-accent.right {
  top: 20%;
  right: 0;
  width: 40px;
  height: 60%;
  clip-path: polygon(0 20%, 100% 0, 100% 100%, 0 80%);
}

.steel-accent.top {
  top: 0;
  left: 20%;
  width: 60%;
  height: 30px;
  clip-path: polygon(0 0, 100% 0, 80% 100%, 20% 100%);
}

.steel-accent.bottom {
  bottom: 0;
  left: 30%;
  width: 40%;
  height: 25px;
  clip-path: polygon(20% 0, 80% 0, 100% 100%, 0 100%);
}

/* Section Separator Styling */
.section-separator {
  position: relative;
  width: 100%;
  height: 120px;
  margin-top: -60px;
  margin-bottom: 30px;
  z-index: 5;
  pointer-events: none;
  overflow: visible;
}

.separator-content {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding-top: 30px;
}

.separator-line {
  height: 3px;
  background: linear-gradient(90deg, transparent, #8f0000, rgba(183, 185, 186, 0.8));
  width: 25%;
  max-width: 300px;
  position: relative;
}

.separator-line.left {
  background: linear-gradient(90deg, transparent, rgba(183, 185, 186, 0.8), #8f0000);
}

.separator-line.right {
  background: linear-gradient(90deg, #8f0000, rgba(183, 185, 186, 0.8), transparent);
}

.separator-line::before {
  content: '';
  position: absolute;
  top: -4px;
  left: 10%;
  right: 10%;
  height: 10px;
  background: repeating-linear-gradient(
    90deg,
    rgba(183, 185, 186, 0.2),
    rgba(183, 185, 186, 0.2) 8px,
    transparent 8px,
    transparent 16px
  );
}

.separator-triangles {
  position: absolute;
  bottom: -10px;
  left: 0;
  width: 100%;
  height: 60px;
  display: flex;
  justify-content: center;
  z-index: 2;
}

.triangle-left, .triangle-right {
  width: 40%;
  height: 100%;
  background: linear-gradient(to bottom right, #1d1e26, transparent);
  clip-path: polygon(0 0, 100% 0, 50% 100%);
}

.triangle-right {
  background: linear-gradient(to bottom left, #1d1e26, transparent);
  clip-path: polygon(0 0, 100% 0, 50% 100%);
}

.separator-emblem {
  width: 90px;
  height: 90px;
  position: relative;
  margin: 0 15px;
}

.emblem-outer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 3px solid #8f0000;
  border-radius: 50%;
  animation: rotate 30s linear infinite;
  box-shadow: 0 0 15px rgba(143, 0, 0, 0.3);
}

.emblem-outer::before, .emblem-outer::after {
  content: '';
  position: absolute;
  width: 10px;
  height: 10px;
  background: rgba(183, 185, 186, 0.8);
  border-radius: 50%;
  top: calc(50% - 5px);
}

.emblem-outer::before {
  left: -5px;
}

.emblem-outer::after {
  right: -5px;
}

.emblem-inner {
  position: absolute;
  top: 15%;
  left: 15%;
  width: 70%;
  height: 70%;
  background: rgba(29, 30, 38, 0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: inset 0 0 10px rgba(0, 0, 0, 0.5);
  z-index: 3;
}

.emblem-icon {
  width: 60%;
  height: 60%;
  background: linear-gradient(135deg, #8f0000, #690000);
  clip-path: polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%);
  transform: rotate(45deg);
  animation: pulse 3s ease-in-out infinite;
}

@keyframes rotate {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes pulse {
  0%, 100% { transform: rotate(45deg) scale(1); }
  50% { transform: rotate(45deg) scale(1.1); }
}

/* Media queries for separator */
@media (max-width: 768px) {
  .section-separator {
    height: 80px;
    margin-top: -40px;
  }
  
  .separator-line {
    width: 20%;
  }
  
  .separator-emblem {
    width: 60px;
    height: 60px;
  }
}
</style>